import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  CheckCircle2, Truck, Calendar, MapPin, AlertTriangle, Download,
  ShieldAlert, Banknote, FileText, Info,
} from 'lucide-react';
import { toast } from 'sonner';
import PlatformDisclaimer from '@/components/PlatformDisclaimer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvoicePayPage = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [load, setLoad] = useState(null);
  const [carrier, setCarrier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    amount: '',
    method: 'ach',
    reference: '',
    paid_date: new Date().toISOString().slice(0, 10),
    payer_email: '',
    payer_name: '',
    notes: '',
  });

  const fetchInv = async () => {
    try {
      const r = await axios.get(`${API}/invoice-pay/${id}`);
      setInvoice(r.data.invoice);
      setLoad(r.data.load);
      setCarrier(r.data.carrier);
      setForm((f) => ({
        ...f,
        amount: r.data.invoice?.total || '',
        payer_email: r.data.invoice?.broker_email || '',
        payer_name: r.data.invoice?.broker_name || '',
      }));
    } catch (e) {
      toast.error('Invoice not found');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInv();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const submitMarkPaid = async () => {
    if (!form.amount || !form.method || !form.reference || !form.paid_date) {
      toast.error('Amount, method, reference, and date are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/invoice-pay/${id}/mark-paid`, {
        amount: parseFloat(form.amount),
        method: form.method,
        reference: form.reference,
        paid_date: form.paid_date,
        payer_email: form.payer_email,
        payer_name: form.payer_name,
        notes: form.notes,
      });
      toast.success('Payment recorded. Carrier and platform have been notified.');
      setOpen(false);
      fetchInv();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading invoice…</div>;
  if (!invoice) return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-amber-400 mb-3" />
          <h2 className="text-xl font-bold mb-2">Invoice not found</h2>
          <p className="text-muted-foreground text-sm">The link may be expired or invalid.</p>
        </CardContent>
      </Card>
    </div>
  );

  const isPaid = invoice.status === 'paid';
  const pdfUrl = `${API}/invoice-pay/${id}/pdf`;

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="invoice-pay-page">
      <div className="flex-1 py-10 px-4">
        <div className="max-w-3xl mx-auto space-y-5">
          <div className="text-center">
            <Truck className="w-8 h-8 mx-auto text-primary mb-2" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Tempus Dispatch · Software Platform</p>
          </div>

          {/* SaaS legal banner */}
          <Card className="border-amber-500/40 bg-amber-500/10" data-testid="not-broker-banner">
            <CardContent className="p-4 flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div className="text-xs leading-relaxed text-amber-100">
                <strong className="text-amber-200">Tempus Dispatch is a software platform — not a freight broker.</strong>{' '}
                This invoice was auto-generated from the load record. Pay the carrier directly using the
                method of your choice (ACH, check, wire). Tempus does not hold, process, or escrow freight payments.
              </div>
            </CardContent>
          </Card>

          {/* PAID CONFIRMATION */}
          {isPaid && (
            <Card className="border-green-500/40 bg-green-500/10" data-testid="invoice-paid-banner">
              <CardContent className="p-6 text-center">
                <CheckCircle2 className="w-12 h-12 mx-auto text-green-400 mb-3" />
                <h2 className="text-2xl font-bold mb-1">Marked as paid</h2>
                <p className="text-sm text-muted-foreground">
                  Recorded via {invoice.payment_method?.toUpperCase()} · Ref{' '}
                  <code>{invoice.payment_reference}</code> · {invoice.paid_date || invoice.paid_at?.slice(0, 10)}
                </p>
              </CardContent>
            </Card>
          )}

          {/* INVOICE CARD */}
          <Card data-testid="invoice-card">
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-2xl">Freight Invoice</CardTitle>
                  <code className="text-xs text-muted-foreground">#{invoice.invoice_number}</code>
                </div>
                <Badge className={isPaid ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-amber-500/20 text-amber-300 border-amber-500/30'}>
                  {isPaid ? 'PAID' : 'UNPAID'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted-foreground text-xs uppercase mb-1">Bill to (Broker / Shipper)</div>
                  <div className="font-medium">{invoice.broker_name || 'Broker'}</div>
                  <div className="text-muted-foreground text-xs">{invoice.broker_email}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-xs uppercase mb-1">Due</div>
                  <div className="font-medium flex items-center gap-1"><Calendar className="w-3 h-3" />{invoice.due_date}</div>
                </div>
              </div>

              {load && (
                <div className="border-t border-border pt-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{load.route}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs text-muted-foreground">
                    {load.equipment_type && <div>Equipment: <span className="text-foreground">{load.equipment_type}</span></div>}
                    {load.miles && <div>Distance: <span className="text-foreground">{load.miles?.toLocaleString()} mi</span></div>}
                    {load.assigned_driver_name && <div>Driver: <span className="text-foreground">{load.assigned_driver_name}</span></div>}
                  </div>
                </div>
              )}

              <div className="border-t border-border pt-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span>Line haul</span><span>${invoice.line_haul?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between text-muted-foreground"><span>Fuel surcharge</span><span>${invoice.fuel_surcharge?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                {invoice.other_charges > 0 && (
                  <div className="flex justify-between text-muted-foreground"><span>Other</span><span>${invoice.other_charges?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></div>
                )}
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-border">
                  <span>Total payable to carrier</span>
                  <span data-testid="invoice-total">${invoice.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
              </div>

              {/* DIRECT PAY INSTRUCTIONS */}
              <div className="border border-blue-500/30 bg-blue-500/5 rounded-lg p-4 space-y-2" data-testid="pay-carrier-direct">
                <div className="flex items-center gap-2 text-blue-300 font-semibold">
                  <Banknote className="w-4 h-4" /> Pay carrier directly
                </div>
                {carrier && (carrier.bank_name || carrier.ach_routing) ? (
                  <div className="text-xs text-zinc-300 space-y-1">
                    <div><span className="text-zinc-500">Beneficiary:</span> {carrier.business_name || carrier.name}</div>
                    {carrier.bank_name && <div><span className="text-zinc-500">Bank:</span> {carrier.bank_name}</div>}
                    {carrier.ach_routing && <div><span className="text-zinc-500">Routing:</span> <code>{carrier.ach_routing}</code></div>}
                    {carrier.ach_account && <div><span className="text-zinc-500">Account:</span> <code>{carrier.ach_account}</code></div>}
                    {carrier.remit_email && <div><span className="text-zinc-500">Remittance email:</span> {carrier.remit_email}</div>}
                  </div>
                ) : (
                  <p className="text-xs text-zinc-400">
                    Carrier ACH details are not on file. Contact the carrier directly at{' '}
                    <strong className="text-zinc-200">{load?.assigned_driver_email || 'the email on the rate confirmation'}</strong>{' '}
                    to request payment instructions (ACH, check, or wire).
                  </p>
                )}
              </div>

              {/* PRIMARY ACTIONS */}
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  variant="outline"
                  size="lg"
                  className="flex-1"
                  onClick={() => window.open(pdfUrl, '_blank')}
                  data-testid="invoice-download-pdf"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download invoice PDF
                </Button>
                {!isPaid && (
                  <Button
                    data-testid="invoice-mark-paid-button"
                    onClick={() => setOpen(true)}
                    size="lg"
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    I paid the carrier — record it
                  </Button>
                )}
              </div>

              <p className="text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1.5">
                <Info className="w-3 h-3" />
                Recording payment creates an audit-trail entry only. Tempus does not transfer funds.
              </p>
            </CardContent>
          </Card>

          {/* VISUALLY SEPARATE: Tempus software fee (carrier's responsibility) */}
          <Card className="border-zinc-700 bg-zinc-900/40" data-testid="platform-fee-card">
            <CardContent className="p-4 flex items-start gap-3">
              <FileText className="w-5 h-5 text-zinc-400 shrink-0 mt-0.5" />
              <div className="text-xs text-zinc-400 leading-relaxed">
                <div className="font-semibold text-zinc-200 mb-0.5">Tempus software subscription — separate invoice</div>
                The carrier pays Tempus 6% of their gross monthly freight revenue (usage-based, self-reported).
                That charge is billed <strong>directly to the carrier</strong> by Tempus Dispatch LLC
                and is unrelated to this freight invoice. You owe Tempus nothing.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* MARK AS PAID DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="mark-paid-modal">
          <DialogHeader>
            <DialogTitle>Record payment to carrier</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <Label className="text-xs">Amount paid (USD) *</Label>
              <Input
                data-testid="mark-paid-amount"
                type="number"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Payment method *</Label>
              <select
                data-testid="mark-paid-method"
                value={form.method}
                onChange={(e) => setForm({ ...form, method: e.target.value })}
                className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-input text-sm"
              >
                <option value="ach">ACH transfer</option>
                <option value="check">Paper check</option>
                <option value="wire">Wire transfer</option>
                <option value="zelle">Zelle</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Reference / Check # / Trace # *</Label>
              <Input
                data-testid="mark-paid-reference"
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="e.g. WIRE-2026-0142"
              />
            </div>
            <div>
              <Label className="text-xs">Date paid *</Label>
              <Input
                data-testid="mark-paid-date"
                type="date"
                value={form.paid_date}
                onChange={(e) => setForm({ ...form, paid_date: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Your email</Label>
              <Input
                data-testid="mark-paid-payer-email"
                type="email"
                value={form.payer_email}
                onChange={(e) => setForm({ ...form, payer_email: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Notes (optional)</Label>
              <Input
                data-testid="mark-paid-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              data-testid="mark-paid-submit"
              onClick={submitMarkPaid}
              disabled={submitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting ? 'Recording…' : 'Record payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PlatformDisclaimer />
    </div>
  );
};

export default InvoicePayPage;
